/**
 * useOpsRetrievalJob
 *
 * Shared hook encapsulating all state and logic common to any modal that calls
 * the OPS retrieval pipeline:
 *   - Preview count (fast) + sample streaming
 *   - KB loading and selection
 *   - Job launch (with Google Patents availability test + OPS fallback dialog)
 *   - Post-launch navigation
 *
 * Used by:
 *   - OpsRetrievalModal  (criteria form → preview panel)
 *   - CpcDownloadModal   (immediate preview from pre-built CPC query)
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  opsRetrievalService,
  type OpsAdvancedQuery,
  type OpsPreviewResult,
  type OpsPreviewSample,
} from '../services/opsRetrievalService';
import { knowledgeBaseService, type KnowledgeBase } from '../services/knowledgeBaseService';

export interface UseOpsRetrievalJobReturn {
  // Preview
  previewResult: OpsPreviewResult | null;
  previewing: boolean;
  previewError: string | null;
  samplesLoading: boolean;
  samplesError: string | null;

  // KB selection
  kbs: KnowledgeBase[];
  kbsLoading: boolean;
  selectedKbId: string;
  setSelectedKbId: (id: string) => void;
  newKbName: string;
  setNewKbName: (name: string) => void;
  kbReady: boolean;

  // Job launch
  launching: boolean;
  launchError: string | null;
  showFallbackDialog: boolean;
  setShowFallbackDialog: (v: boolean) => void;
  testingGooglePatents: boolean;

  // Actions
  startPreview: (query: OpsAdvancedQuery) => Promise<void>;
  handleLaunch: (mode: 'list' | 'full', query: OpsAdvancedQuery, limit?: number | null) => Promise<void>;
  handleFallbackConfirm: (query: OpsAdvancedQuery) => Promise<void>;
  resetPreview: () => void;
}

export function useOpsRetrievalJob(subProjectId: string): UseOpsRetrievalJobReturn {
  const navigate = useNavigate();

  // ── Preview state ────────────────────────────────────────────────────────────
  const [previewResult, setPreviewResult] = useState<OpsPreviewResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [samplesLoading, setSamplesLoading] = useState(false);
  const [samplesError, setSamplesError] = useState<string | null>(null);
  const samplesAbortRef = useRef<AbortController | null>(null);

  // ── KB state ─────────────────────────────────────────────────────────────────
  const [kbs, setKbs] = useState<KnowledgeBase[]>([]);
  const [kbsLoading, setKbsLoading] = useState(false);
  const [selectedKbId, setSelectedKbId] = useState('');
  const [newKbName, setNewKbName] = useState('');

  // ── Launch state ─────────────────────────────────────────────────────────────
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [showFallbackDialog, setShowFallbackDialog] = useState(false);
  const [testingGooglePatents, setTestingGooglePatents] = useState(false);

  // Pending launch args when the fallback dialog is shown
  const pendingLaunchRef = useRef<{ query: OpsAdvancedQuery; limit?: number | null } | null>(null);

  // ── Derived ──────────────────────────────────────────────────────────────────
  const kbReady =
    selectedKbId === '__new__' ? newKbName.trim().length > 0 : selectedKbId.length > 0;

  // ── Load KBs for this sub-project ────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setKbsLoading(true);
    knowledgeBaseService
      .getAll()
      .then((all) => {
        if (cancelled) return;
        const spKbs = all.filter((kb) => kb.sub_project_id === subProjectId);
        setKbs(spKbs);
        if (spKbs.length > 0) setSelectedKbId(spKbs[0].id);
      })
      .catch(() => { if (!cancelled) setKbs([]); })
      .finally(() => { if (!cancelled) setKbsLoading(false); });
    return () => { cancelled = true; };
  }, [subProjectId]);

  // ── Abort samples stream on unmount ──────────────────────────────────────────
  useEffect(() => {
    return () => { samplesAbortRef.current?.abort(); };
  }, []);

  // ── Preview: count + stream samples ──────────────────────────────────────────
  const startPreview = useCallback(async (query: OpsAdvancedQuery) => {
    samplesAbortRef.current?.abort();
    setPreviewing(true);
    setPreviewError(null);
    setSamplesError(null);
    setSamplesLoading(false);

    try {
      const countResult = await opsRetrievalService.previewCount(query);
      setPreviewResult({ ...countResult, sample: [] });
      setPreviewing(false);

      if (countResult.total_count > 0) {
        setSamplesLoading(true);
        samplesAbortRef.current = opsRetrievalService.streamSamples(
          countResult.cql,
          countResult.total_count,
          (sample: OpsPreviewSample) =>
            setPreviewResult((prev) =>
              prev ? { ...prev, sample: [...prev.sample, sample] } : prev
            ),
          () => setSamplesLoading(false),
          (message) => { setSamplesError(message); setSamplesLoading(false); },
        );
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string };
      setPreviewError(e?.response?.data?.detail ?? e?.message ?? 'Preview failed');
      setPreviewing(false);
    }
  }, []);

  const resetPreview = useCallback(() => {
    samplesAbortRef.current?.abort();
    setPreviewResult(null);
    setPreviewError(null);
    setSamplesLoading(false);
    setSamplesError(null);
    setLaunchError(null);
  }, []);

  // ── Job launch ────────────────────────────────────────────────────────────────
  const doLaunchJob = useCallback(
    async (
      mode: 'list' | 'full',
      query: OpsAdvancedQuery,
      textSource?: 'google_patents' | 'ops',
      limit?: number | null,
    ) => {
      if (!previewResult || !kbReady) return;
      try {
        const params: Parameters<typeof opsRetrievalService.startJob>[0] = {
          sub_project_id: subProjectId,
          query,
          mode,
          cql: previewResult.cql,
          total_count: previewResult.effective_count,
        };
        if (textSource) params.text_source = textSource;
        if (limit != null && limit > 0) params.max_patents = limit;
        if (selectedKbId === '__new__') {
          params.new_kb_name = newKbName.trim();
        } else {
          params.target_kb_id = selectedKbId;
        }
        const job = await opsRetrievalService.startJob(params);
        navigate(`/ops-retrieval-progress/${job.id}`);
      } catch (err: unknown) {
        const e = err as { response?: { data?: { detail?: string } }; message?: string };
        setLaunchError(e?.response?.data?.detail ?? e?.message ?? 'Failed to start job');
        setLaunching(false);
      }
    },
    [previewResult, kbReady, subProjectId, selectedKbId, newKbName, navigate],
  );

  const handleLaunch = useCallback(
    async (mode: 'list' | 'full', query: OpsAdvancedQuery, limit?: number | null) => {
      if (!previewResult || !kbReady) return;
      setLaunching(true);
      setLaunchError(null);

      if (mode === 'full') {
        const sampleId = previewResult.sample?.[0]?.document_id;
        if (sampleId) {
          setTestingGooglePatents(true);
          try {
            const test = await opsRetrievalService.testGooglePatents(sampleId);
            setTestingGooglePatents(false);
            if (!test.available) {
              pendingLaunchRef.current = { query, limit };
              setLaunching(false);
              setShowFallbackDialog(true);
              return;
            }
            await doLaunchJob(mode, query, 'google_patents', limit);
          } catch {
            setTestingGooglePatents(false);
            pendingLaunchRef.current = { query, limit };
            setLaunching(false);
            setShowFallbackDialog(true);
          }
          return;
        }
        // No sample available to test — default to OPS
        await doLaunchJob(mode, query, 'ops', limit);
        return;
      }

      await doLaunchJob(mode, query, undefined, limit);
    },
    [previewResult, kbReady, doLaunchJob],
  );

  const handleFallbackConfirm = useCallback(
    async (query: OpsAdvancedQuery) => {
      setShowFallbackDialog(false);
      setLaunching(true);
      const pending = pendingLaunchRef.current;
      const q = pending?.query ?? query;
      const limit = pending?.limit;
      pendingLaunchRef.current = null;
      await doLaunchJob('full', q, 'ops', limit);
    },
    [doLaunchJob],
  );

  return {
    previewResult,
    previewing,
    previewError,
    samplesLoading,
    samplesError,
    kbs,
    kbsLoading,
    selectedKbId,
    setSelectedKbId,
    newKbName,
    setNewKbName,
    kbReady,
    launching,
    launchError,
    showFallbackDialog,
    setShowFallbackDialog,
    testingGooglePatents,
    startPreview,
    handleLaunch,
    handleFallbackConfirm,
    resetPreview,
  };
}
